import { ReadingContextInterface } from "context/initialState/Readings";
import { READING_TYPES } from "helpers/appConstants";
import { formatSpirometry } from "helpers/variables";
import moment from "moment";
import Highcharts from 'highcharts';

export const getYAxisRange = (readingType: string, hasData: boolean = true) => {
  switch (readingType) {
    case "systolic":
      return { min: 70, max: 200 };
    case "diastolic":
      return { min: 40, max: 140 };
    case "Blood Pressure":
        return { min: 40, max: 200 };
    case "Blood Glucose":
      return { min: 50, max: 300 };
    case "Weight":
      return { min: 30, max: 200 };
    case "FEV1":
      return { min: 0, max: 6 };
    case "FVC":
      return { min: 0, max: 7 };
    case "FEV1_FVC":
      return { min: 0, max: 1.5 };
    case "PEF":
        return { min: 0, max: 12 };
    case "Spirometry":
      return { min: 0, max: 7 };
    case "Oxygen Saturation":
      return { min: 60, max: 100 };
    case "Temperature":
      return { min: 80, max: 120 };
    case "activity":
      return { min: 0, max: hasData ? undefined : 15000 };
    case "Sleep":
      return { min: 0, max: hasData ? undefined : 12 };
    case "Heart Rate":
      return { min: 20, max: 200 };
    default:
      return { min: undefined, max: undefined };
  }
};

export interface Reading {
  id: string;
  measuredAt: string;
  type: string;
  metrics: Record<string, unknown>;
}
export interface ChartProps {
  type: string;
};

export interface GraphInterface {
  measuredAt: number | undefined;
  systolic?: number | undefined;
  diastolic?: number | undefined;
  glucose?: number | undefined;
  spo2?: number | undefined;
  FVC?: number | undefined;
  FEV1?: number | undefined;
  FEV1_FVC?: number | undefined;
  PEF?: number | undefined;
  pulse?: number | undefined;
  temp?: number | undefined;
  weight?: number | undefined;
  activity?: number | undefined;
  sleep?: number | undefined;
};

export interface Thresholds {
  lowWarning?: number;
  highWarning?: number;
  lowEmergency?: number;
  highEmergency?: number;
};
export interface BPThresholds {
  systolic: Thresholds;
  diastolic: Thresholds;
};
export interface SpirThresholds {
  FEV1: Thresholds;
  FVC: Thresholds;
  FEV1_FVC: Thresholds;
  PEF: Thresholds;
};

export const {
  bloodGlucose,
  bloodPressure,
  temperature,
  weight,
  heartRate,
  oxygenSaturation,
  spirometry,
  activity,
  sleep
} = READING_TYPES;

let key = "";
export const formatChart = (
  readings: ReadingContextInterface[],
  type: string
): GraphInterface[] => {
  let graphData: GraphInterface[] = [];
  readings.map((reading) => {
    const timeStamp = moment.utc(reading.measuredAt).local().valueOf();
    switch (type) {
      case heartRate.display:
        graphData.push({
          measuredAt: timeStamp,
          pulse: reading.metrics?.pulse
        });
        break;
      case bloodPressure.display:
        graphData.push({
          measuredAt: timeStamp,
          systolic: reading.metrics?.systolic,
          diastolic: reading.metrics?.diastolic
        });
        break;
      case bloodGlucose.display:
        graphData.push({
          measuredAt: timeStamp,
          glucose: reading.metrics?.glucose
        });
        break;
      case oxygenSaturation.display:
        graphData.push({
          measuredAt: timeStamp,
          spo2: reading.metrics?.spo2
        });
        break;
      case temperature.display:
        graphData.push({
          measuredAt: timeStamp,
          temp: reading.metrics?.temp
        });
        break;
      case weight.display:
        graphData.push({
          measuredAt: timeStamp,
          weight: reading.metrics?.weight
        });
        break;
      case activity.display:
        graphData.push({
          measuredAt: timeStamp,
          activity: reading.metrics?.steps
        });
        break;
      case sleep.display:
        graphData.push({
          measuredAt: timeStamp,
          sleep: reading.metrics?.sleep
        });
        break;
      case spirometry.display:
        const spirReading = formatSpirometry(reading);
        const { FEV1, FVC } = spirReading;

        const parsedFEV1 = spirReading.FEV1 ? parseFloat(spirReading.FEV1) : undefined;
        const parsedFVC = spirReading.FVC ? parseFloat(spirReading.FVC) : undefined;
        const parsedFEV1_FVC = spirReading.FEV1_FVC ? parseFloat(spirReading.FEV1_FVC) : undefined;
        const parsedPEF = spirReading.PEF ? parseFloat(spirReading.PEF) : undefined;

        if (spirReading.device === "Spirobank Smart") {
          key = "FEV1_FVC";
          graphData.push({
            measuredAt: timeStamp,
            FEV1: parsedFEV1,
            FVC: parsedFVC,
            FEV1_FVC: parsedFEV1_FVC,
            PEF: parsedPEF,
          });
        } else if (spirReading.device === "Vitalograph 40372") {
          key = "FEV1";
          graphData.push({
            measuredAt: timeStamp,
            FEV1: parsedFEV1
          });
        }

        break;
    }
  });
  return graphData;
};

export const formatSeries = (
  type: string, data: any[], 
  thresholds: Thresholds | undefined, 
  fieldOption?: "systolic" | "diastolic" | "FEV1" | "FVC" | "FEV1_FVC" | "PEF"
) => {
  const getZones = (thresholds: Thresholds | undefined) => {
      if (!thresholds) return [];
      return [
        { value: thresholds.lowEmergency, color: "#d32f2f" },
        { value: thresholds.lowWarning, color: "#fbc02d" },
        { value: thresholds.highWarning, color: "#02d90d" },
        { value: thresholds.highEmergency, color: "#fbc02d" },
        { color: "#d32f2f" }
      ];
  };
  const buildSeries = (
    name: string,
    key: string,
    data: any[],
    thresholds?: Thresholds
  ) => ({
    name,
    data: data
      .filter(reading => reading[key] != null && reading[key] !== "" && !isNaN(Number(reading[key])))
      .map(reading => [new Date(reading.measuredAt).getTime(), reading[key]])
      .sort((a, b) => a[0] - b[0]),
    zones: thresholds ? getZones(thresholds) : undefined,
    marker: { enabled: true },
  });
  switch(type) {
    case bloodPressure.display: 
      if(fieldOption === "systolic"){
        return [buildSeries("Systolic", "systolic", data, thresholds)];
      } else if(fieldOption === "diastolic"){
        return [buildSeries("Diastolic", "diastolic", data, thresholds)];
      } else {
       return;
      }
    case spirometry.display:
      if(fieldOption === "FEV1") {
        return [buildSeries("FEV1", "FEV1", data, thresholds)];
      } else if(fieldOption === "FVC") {
        return [buildSeries("FVC", "FVC", data, thresholds)];
      } else if(fieldOption === "FEV1_FVC") {
        return [buildSeries("FEV1_FVC", "FEV1_FVC", data, thresholds)];
      } else if(fieldOption === "PEF") {
        return [buildSeries("PEF", "PEF", data, thresholds)];
      }else {
        return;
      }
    case bloodGlucose.display:
      return [buildSeries("Glucose", "glucose", data, thresholds)];
    case oxygenSaturation.display: 
      return [buildSeries("SpO2", "spo2", data, thresholds)];
    case temperature.display:
      return [buildSeries("Temperature", "temp", data, thresholds)];
    case weight.display:
      return [buildSeries("Weight", "weight", data, thresholds)];
    case activity.display:
      return [buildSeries("Activity", "activity", data, thresholds)];
    case sleep.display:
      return [buildSeries("Sleep", "sleep", data, thresholds)];
    case heartRate.display:
      return [buildSeries("Pulse", "pulse", data, thresholds)];
    default:
      return[];
  }
};

export const normalizeThresholds = (
  thresholdList: any[],
  readingType: string
): Thresholds | BPThresholds | SpirThresholds => {
  const applyBreachTo = (target: Thresholds, breachType: string, metrics: any) => {
    if (!metrics) return;
    if (breachType === "high") {
      if (metrics.high !== undefined) target.highEmergency = metrics.high;
      if (metrics.low !== undefined)  target.lowEmergency  = metrics.low;
    } else if (breachType === "moderate") {
      if (metrics.high !== undefined) target.highWarning = metrics.high;
      if (metrics.low !== undefined)  target.lowWarning  = metrics.low;
    } else if (breachType === "low") {
      if (metrics.low !== undefined) target.lowEmergency = metrics.low;
    } else if (breachType === "severe" || breachType === "emergency") {
      if (metrics.high !== undefined) target.highEmergency = metrics.high;
      if (metrics.low !== undefined)  target.lowEmergency  = metrics.low;
    }
  };

  if (readingType === "Blood Pressure") {
    const systolic: Thresholds = {};
    const diastolic: Thresholds = {};

    thresholdList.forEach((threshold) => {
      const breachType = threshold.breachType;
      const val = threshold.value || {};

      const sMetrics = val.systolic ?? null;
      const dMetrics = val.diastolic ?? null;

      if (!sMetrics && !dMetrics) {
        const nestedKey = Object.keys(val)[0];
        const nested = val[nestedKey];
        if (nested) {
          if (nested.systolic || nested.diastolic) {
            if (nested.systolic) applyBreachTo(systolic, breachType, nested.systolic);
            if (nested.diastolic) applyBreachTo(diastolic, breachType, nested.diastolic);
            return;
          }
        }
      }

      if (sMetrics) applyBreachTo(systolic, breachType, sMetrics);
      if (dMetrics) applyBreachTo(diastolic, breachType, dMetrics);
    });

    return { systolic, diastolic };
  }

  if (readingType === "Spirometry") {
    const FEV1: Thresholds = {};
    const FVC: Thresholds = {};
    const FEV1_FVC: Thresholds = {};
    const PEF: Thresholds = {};

    thresholdList.forEach((threshold) => {
      const breachType = threshold.breachType;
      const val = threshold.value || {};

      const fev1Metrics = val.FEV1 ?? null;
      const fvcMetrics = val.FVC ?? null;
      const fev1_fvcMetrics = val.FEV1_FVC ?? null;
      const pefMetrics = val.PEF ?? null;

      if (!fev1Metrics && !fvcMetrics && !fev1_fvcMetrics && !pefMetrics) {
        const nestedKey = Object.keys(val)[0];
        const nested = val[nestedKey];
        if (nested) {
          if (nested.FEV1 || nested.FVC || nested.FEV1_FVC || nested.PEF) {
            if (nested.FEV1) applyBreachTo(FEV1, breachType, nested.FEV1);
            if (nested.FVC) applyBreachTo(FVC, breachType, nested.FVC);
            if (nested.FEV1_FVC) applyBreachTo(FEV1_FVC, breachType, nested.FEV1_FVC);
            if (nested.PEF) applyBreachTo(PEF, breachType, nested.PEF);
            return;
          }
        }
      }

      if (fev1Metrics) applyBreachTo(FEV1, breachType, fev1Metrics);
      if (fvcMetrics) applyBreachTo(FVC, breachType, fvcMetrics);
      if (fev1_fvcMetrics) applyBreachTo(FEV1_FVC, breachType, fev1_fvcMetrics);
      if (pefMetrics) applyBreachTo(PEF, breachType, pefMetrics);
    });

    return { FEV1, FVC, FEV1_FVC, PEF };
  }

  const result: Thresholds = {};

  thresholdList.forEach((threshold) => {
    const breachType = threshold.breachType;
    const val = threshold.value || {};

    const key = Object.keys(val)[0];
    const metrics = val[key] ?? val;

    applyBreachTo(result, breachType, metrics);
  });

  return result;
};

export const getThresholdPlotBands = (thresholds: Thresholds | undefined, yMin: number, yMax: number) => {
  if (!thresholds) return [];

  return [
    {
      from: thresholds.highEmergency,
      to: yMax,
      color: Highcharts.color("#d32f2f").setOpacity(0.4).get(),
    },
    {
      from: thresholds.highWarning,
      to: thresholds.highEmergency,
      color: Highcharts.color("#fbc02d").setOpacity(0.4).get(),
    },
    {
      from: thresholds.lowWarning,
      to: thresholds.highWarning,
      color: Highcharts.color("#02d90dff").setOpacity(0.3).get(),
    },
    {
      from: thresholds.lowEmergency,
      to: thresholds.lowWarning,
      color: Highcharts.color("#fbc02d").setOpacity(0.4).get(),
    },
    {
      from: yMin,
      to: thresholds.lowEmergency,
      color: Highcharts.color("#d32f2f").setOpacity(0.4).get(),
    }
  ];
};

export const getXAxisConfig = (
  range: "day" | "week" | "month" | "year",
  start: number,
  end: number
): Highcharts.XAxisOptions => {
  let tickInterval: number;
  let labelFormat: string;

  switch (range) {
    case "day":
      const startOfDay = moment(start).startOf("day").valueOf();
      const endOfDay = moment(start).endOf("day").valueOf(); 

      return {
        type: "datetime",
        min: moment(start).startOf("day").valueOf(),
        max: moment(end).endOf("day").valueOf(),
        tickInterval: 3600 * 1000,
        tickPositioner: function () {
          const positions: number[] = [];
          let current = startOfDay;
          while (current <= endOfDay) {
            positions.push(current);
            current += 3600 * 1000;
          }
          return positions;
        },
        labels: {
          rotation: -45,
          formatter: function () {
            return Highcharts.dateFormat("%H:%M", Number(this.value));
          }
        }
      };
    case "week":
      tickInterval = 24 * 3600 * 1000;
      labelFormat = "%b %e";
      break;
    case "month": 
      const startOfMonth = moment(start).startOf("month").startOf("day").valueOf();
      const endOfMonth = moment(start).endOf("month").endOf("day").valueOf();

      return {
        type: "datetime",
        min: startOfMonth,
        max: endOfMonth,
        tickPositioner: function () {
          const positions: number[] = [];
          const MS_DAY = 24 * 3600 * 1000;
          for (let t = startOfMonth; t <= endOfMonth; t += MS_DAY) {
            positions.push(t);
          }
          return positions;
        },
        labels: {
          rotation: -45,
          formatter: function () {
            return Highcharts.dateFormat("%b %e", Number(this.value));
          }
        }
      };
    case "year":
      const startOfYear = moment(start).startOf("year").startOf("day").valueOf();
      const endOfYear = moment(start).endOf("year").endOf("day").valueOf();

      return {
        type: "datetime",
        min: startOfYear,
        max: endOfYear,
        tickPositioner: function () {
          const positions: number[] = [];

          let current = startOfYear;
          while (current <= endOfYear) {
            positions.push(current);
            current = moment(current).add(1, "month").startOf("month").valueOf();
          }

          return positions;
        },
        labels: {
          rotation: -45,
          formatter: function () {
            return Highcharts.dateFormat("%b", Number(this.value));
          }
        }
      };
    default:
      tickInterval = 1 * 3600 * 1000;
      labelFormat = "%H:%M";
  }

  return {
    type: "datetime",
    min: start,
    max: end,
    tickInterval,
    tickPixelInterval: 0,
    tickPositioner: function () {
      const positions: number[] = [];
      for (let t = start; t <= end; t += tickInterval) {
        positions.push(t);
      }
      return positions;
    },
    labels: {
      formatter: function () {
        return Highcharts.dateFormat(labelFormat, Number(this.value));
      }
    }
  };
};

export function getRange(mode: "day" | "week" | "month" | "year", refDate: Date) {
  switch (mode) {
    case "day":
      return {
        start: moment(refDate).startOf("day"),
        end: moment(refDate).endOf("day"),
      };
    case "week":
      return {
        start: moment(refDate).startOf("week"),
        end: moment(refDate).endOf("week"),
      };
    case "month":
      return {
        start: moment(refDate).startOf("month"),
        end: moment(refDate).endOf("month"),
      };
    case "year":
      return {
        start: moment(refDate).startOf("year"),
        end: moment(refDate).endOf("year"),
      };
  }
};

export function shiftRange(mode: "day" | "week" | "month" | "year", refDate: Date, direction: number) {
  switch (mode) {
    case "day": return moment(refDate).add(direction, "days").toDate();
    case "week": return moment(refDate).add(direction, "weeks").toDate();
    case "month": return moment(refDate).add(direction, "months").toDate();
    case "year": return moment(refDate).add(direction, "years").toDate();
  }
};